import { GmailLabelSelector } from '@agentic-flow-studio/shared/components/clinic/GmailLabelSelector';
import { OutlookLabelSelector } from '@agentic-flow-studio/shared/components/clinic/OutlookLabelSelector';
import { cn } from '@agentic-flow-studio/shared/lib/utils';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import { Link2, Minus, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useClinicCategories } from '@/hooks/useClinic';
import { trpc } from '@/utils/trpc';

/**
 * Account-grouped mail-tag management — replaces the old flat, single-account
 * "Specific Label Overrides" and "Map Labels to Categories" tabs entirely
 * (settings-tab cutover). One tag-centric surface: which account(s) a tag is
 * connected to (`GmailLabelSelector`/`OutlookLabelSelector` per account by
 * `provider` — mail-tags redesign decision 4), its importance weight, and
 * its category memberships — all keyed by `tagId`, not raw label id, so two
 * accounts' colliding label ids/names no longer share config.
 * See docs/gmail-labels-redesign-spec.md §5.
 */
export function MailTagsSettings({ isReadOnly }: { isReadOnly: boolean }) {
  const utils = trpc.useUtils();
  const tagsQuery = trpc.mailTags.list.useQuery();
  const accountsQuery = trpc.gmail.getConnectedExternalAccounts.useQuery();

  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [connectingTagId, setConnectingTagId] = useState<number | null>(null);
  const [connectingAccountId, setConnectingAccountId] = useState<number | null>(null);

  const createTag = trpc.mailTags.create.useMutation({
    onSuccess: () => {
      setNewTagName('');
      utils.mailTags.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const renameTag = trpc.mailTags.rename.useMutation({
    onSuccess: (result) => {
      setEditingTagId(null);
      if (result.syncErrors?.length) {
        toast.warning(
          `Renamed, but ${result.syncErrors.length} connected account(s) couldn't be updated — see console.`
        );
        console.error('Mail tag rename sync errors:', result.syncErrors);
      }
      utils.mailTags.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteTag = trpc.mailTags.delete.useMutation({
    onSuccess: () => utils.mailTags.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const addProjection = trpc.mailTags.addProjection.useMutation({
    onSuccess: () => {
      setConnectingTagId(null);
      setConnectingAccountId(null);
      utils.mailTags.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const removeProjection = trpc.mailTags.removeProjection.useMutation({
    onSuccess: () => utils.mailTags.list.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const providerLabelsQuery = trpc.mailTags.listProviderLabels.useQuery(
    { accountId: connectingAccountId ?? 0 },
    { enabled: connectingAccountId != null }
  );

  const weightsQuery = trpc.mailTags.getWeights.useQuery();
  const mappingsQuery = trpc.mailTags.getCategoryMappings.useQuery();
  const clinicCategoriesQuery = useClinicCategories();

  const setWeight = trpc.mailTags.setWeight.useMutation({
    onSuccess: () => utils.mailTags.getWeights.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const setCategoryMappings = trpc.mailTags.setCategoryMappings.useMutation({
    onSuccess: () => utils.mailTags.getCategoryMappings.invalidate(),
    onError: (err) => toast.error(err.message),
  });

  const tags = tagsQuery.data?.items ?? [];
  const accounts = accountsQuery.data ?? [];
  const connectingAccount = accounts.find((a) => a.id === connectingAccountId);
  const weights = weightsQuery.data ?? [];
  const tagMappings = mappingsQuery.data ?? [];
  const clinicCategories = clinicCategoriesQuery.data ?? [];

  const updateWeight = (tagId: number, delta: number) => {
    const current = weights.find((w) => w.tagId === tagId)?.weight ?? 0;
    const next = Math.max(-10, Math.min(10, current + delta));
    setWeight.mutate({ tagId, weight: next });
  };

  const toggleCategoryMapping = (tagId: number, categoryId: number) => {
    const currentCategoryIds = tagMappings
      .filter((m) => m.tagId === tagId)
      .map((m) => m.categoryId);
    const isMapped = currentCategoryIds.includes(categoryId);
    const nextCategoryIds = isMapped
      ? currentCategoryIds.filter((id) => id !== categoryId)
      : [...currentCategoryIds, categoryId];
    setCategoryMappings.mutate({ tagId, categoryIds: nextCategoryIds });
  };

  return (
    <Card className="rounded-panel border-border bg-card">
      <CardHeader>
        <CardTitle>Mail Tags</CardTitle>
        <CardDescription>
          Internal tags, optionally connected to a Gmail label or Outlook category per account. A
          tag with no connections applies across every account; a connected tag only applies to
          threads in the account(s) it&apos;s connected to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isReadOnly && (
          <div className="flex gap-2">
            <Input
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="New tag name…"
              className="max-w-xs"
            />
            <Button
              size="sm"
              disabled={!newTagName.trim() || createTag.isPending}
              onClick={() => createTag.mutate({ name: newTagName.trim() })}
            >
              <Plus className="h-4 w-4 mr-1" /> Add tag
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {tags.map((tag) => {
            const connectedAccountIds = new Set(tag.projections.map((p) => p.accountId));
            const availableAccounts = accounts.filter((a) => !connectedAccountIds.has(a.id));

            return (
              <div
                key={tag.id}
                className="p-4 rounded-xl border border-border/50 bg-background/50 space-y-3"
              >
                <div className="flex items-center justify-between gap-2">
                  {editingTagId === tag.id ? (
                    <div className="flex flex-col gap-1 flex-1">
                      <div className="flex items-center gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="h-8 max-w-xs"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!editingName.trim() || renameTag.isPending}
                          onClick={() =>
                            renameTag.mutate({ tagId: tag.id, name: editingName.trim() })
                          }
                        >
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTagId(null)}>
                          Cancel
                        </Button>
                      </div>
                      {tag.projections.some(
                        (p) => accounts.find((a) => a.id === p.accountId)?.provider === 'microsoft'
                      ) && (
                        <p className="text-[10px] text-foreground">
                          Connected to an Outlook category — Outlook categories are keyed by name,
                          so this rename won&apos;t sync there (see #47). Gmail-connected accounts
                          will still update.
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="font-medium text-sm flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </span>
                  )}

                  {!isReadOnly && editingTagId !== tag.id && (
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditingTagId(tag.id);
                          setEditingName(tag.name);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteTag.mutate({ tagId: tag.id })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {tag.projections.map((projection) => {
                    const account = accounts.find((a) => a.id === projection.accountId);
                    return (
                      <Badge
                        key={projection.id}
                        variant="secondary"
                        className="gap-1.5 text-[10px]"
                      >
                        {account?.email ?? `account #${projection.accountId}`} ·{' '}
                        {projection.providerLabelName}
                        {!isReadOnly && (
                          <button
                            onClick={() => removeProjection.mutate({ id: projection.id })}
                            className="hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    );
                  })}
                  {tag.projections.length === 0 && (
                    <span className="text-[11px] text-muted-foreground">
                      Not connected — applies to every account
                    </span>
                  )}
                </div>

                {!isReadOnly && availableAccounts.length > 0 && (
                  <div>
                    {connectingTagId === tag.id ? (
                      <div className="space-y-2 pt-2 border-t border-border/50">
                        <div className="flex flex-wrap gap-1.5">
                          {availableAccounts.map((account) => (
                            <button
                              key={account.id}
                              onClick={() => setConnectingAccountId(account.id)}
                              className={
                                connectingAccountId === account.id
                                  ? 'px-2 py-1 rounded-md text-[11px] font-medium border bg-primary text-primary-foreground border-primary'
                                  : 'px-2 py-1 rounded-md text-[11px] font-medium border bg-muted/30 border-transparent hover:border-primary/30'
                              }
                            >
                              {account.email}
                              <span className="ml-1 opacity-60">
                                ({account.provider === 'microsoft' ? 'Outlook' : 'Gmail'})
                              </span>
                            </button>
                          ))}
                        </div>
                        {connectingAccountId != null &&
                          (() => {
                            const options = (providerLabelsQuery.data ?? [])
                              .filter((l) => l.type === 'user')
                              .map((l) => ({ providerLabelId: l.providerLabelId, name: l.name }));
                            const onSelect = (label: { providerLabelId: string; name: string }) =>
                              addProjection.mutate({
                                tagId: tag.id,
                                accountId: connectingAccountId,
                                providerLabelId: label.providerLabelId,
                                providerLabelName: label.name,
                              });

                            return connectingAccount?.provider === 'microsoft' ? (
                              <OutlookLabelSelector categories={options} onSelect={onSelect} />
                            ) : (
                              <GmailLabelSelector labels={options} onSelect={onSelect} />
                            );
                          })()}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setConnectingTagId(null);
                            setConnectingAccountId(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => setConnectingTagId(tag.id)}
                      >
                        <Link2 className="h-3.5 w-3.5" /> Connect a label or category
                      </Button>
                    )}
                  </div>
                )}

                <div className="pt-2 border-t border-border/50 flex items-center justify-between gap-3">
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    Importance weight
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => updateWeight(tag.id, -1)}
                      disabled={isReadOnly}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <div
                      className={cn(
                        'w-6 text-center text-xs font-semibold',
                        (weights.find((w) => w.tagId === tag.id)?.weight ?? 0) > 0
                          ? 'text-foreground'
                          : (weights.find((w) => w.tagId === tag.id)?.weight ?? 0) < 0
                            ? 'text-foreground'
                            : ''
                      )}
                    >
                      {weights.find((w) => w.tagId === tag.id)?.weight ?? 0}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => updateWeight(tag.id, 1)}
                      disabled={isReadOnly}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {clinicCategories.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {clinicCategories.map((cat) => {
                      const isMapped = tagMappings.some(
                        (m) => m.tagId === tag.id && m.categoryId === cat.id
                      );
                      return (
                        <button
                          key={cat.id}
                          onClick={() => toggleCategoryMapping(tag.id, cat.id)}
                          disabled={isReadOnly}
                          className={cn(
                            'px-2 py-1 rounded-md text-[10px] font-semibold border transition-all',
                            isMapped
                              ? 'text-primary-foreground border-transparent'
                              : 'bg-muted/30 border-transparent hover:border-primary/30'
                          )}
                          style={isMapped ? { backgroundColor: cat.color } : undefined}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {tags.length === 0 && !tagsQuery.isLoading && (
            <p className="text-sm text-muted-foreground">No mail tags yet.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
